/**
 * PROJECT-EDIT.WIDGET
 * Load all project wizard
 */

import { connect } from "react-redux"
import { withTranslation, WithTranslation } from "react-i18next"
import Space from "@/components/space"
import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { STATUS_LOADED, STATUS_LOADING, STATUS_SAVED, STATUS_SAVING, STATUS_SEARCHING } from "@/redux/_status.types"
import {
  faCalendar,
  faCalendarDays,
  faChartBar,
  faClipboardList,
  faCommentDots,
  faEdit,
  faEnvelope,
  faFileImport,
  faFilter,
  faLanguage,
  faLayerGroup,
  faLock,
  faListCheck,
  faPaperPlane,
  faQrcode,
  faShareNodes,
  faSms,
  faStop,
  faTrash,
  faUserCog,
  faUsers,
  faVial,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons"
import Button from "@/components/button"
import PageLoader from "@/components/page-loader"
import { Template, TemplateState } from "@/redux/template.types"
import { countAlertErrors, countAlertWarnings, ProjectState, ProjectStep } from "@/redux/project.types"
import { store } from "@/index"
import {
  projectCheckCalendar,
  projectCheckEmails,
  projectCheckFilters,
  projectCheckLanguages,
  projectCheckMessage,
  projectCheckQrcode,
  projectCheckImport,
  projectCheckRecipients,
  projectCheckSegment,
  projectCheckSms,
  projectCheckTemplate,
  projectEdit,
  projectStatus,
  projectUpdate
} from "@/redux/project.actions"
import { templateActivate, templateFetchOne } from "@/redux/template.actions"
import { sendingFetch, sendingGet } from "@/redux/sending.actions"
import {
  userFetchCount,
  userFetchRecipients,
  userGet,
  userGetCount,
  userGetCountSearch,
  userGetExcluded,
  userStatus
} from "@/redux/user.actions"
import { Sending, SendingState } from "@/redux/sending.types"
import { UserState } from "@/redux/user.types"
import ProjectTemplateWidget from "./project-template.widget"
import ProjectModesWidget from "./project-modes.widget"
import ProjectImportWidget from "./project-import.widget"
import ProjectRecipientsWidget from "./project-recipients.widget"
import ProjectSegmentWidget from "./project-segment.widget"
import ProjectLanguagesWidget from "./project-languages.widget"
import ProjectEmailsWidget from "./project-emails.widget"
import ProjectTestModal from "@/modals/project-test.modal"
import ProjectMessageWidget from "./project-message.widget"
import PageHeader from "@/components/page-header"
import ProjectLaunchModal from "@/modals/project-launch.modal"
import { goToAlertTarget } from "@/utils/alert-target.utils"
import ProjectLaunchReviewModal, { countRowErrors, countRowWarnings, getSeverity, LaunchReviewRow } from "@/modals/project-launch-review.modal"
import ProjectReviewToast from "@/widgets/project-review-toast.widget"
import { motion } from "framer-motion"
import { useAnimationsDisabled } from "@/utils/animation-mode.utils"
import ProjectEditSkeletonWidget from "@/widgets/skeletons/project-edit-skeleton.widget"
import ProjectReviewLauncher from "@/widgets/project-review-launcher.widget"
import ProjectCloseSurveyModal from "@/modals/project-close-survey.modal"
import { surveyActivate } from "@/redux/survey.actions"
import { Survey, SurveyState } from "@/redux/survey.types"
import Link from "@/components/link"
import ProjectDeleteModal from "@/modals/project-delete.modal"
import ProjectChecklistWidget from "./project-checklist.widget"
import StepCard from "@/components/step-card"
import {
  selectRecipientsFilter,
  recipientsViewActions,
  toRecipientsCounts
} from "@/features/survey/entities/recipients-view/recipients-view.store"
import { ProjectStepContext } from "@/components/project-step-context"
import ProjectQrcodeWidget from "./project-qrcode.widget"
import ProjectRenameModal from "@/modals/project-rename.modal"
import { emailFetchByProject, emailGet } from "@/redux/email.actions"
import { Email, EmailState } from "@/redux/email.types"
import ProjectCalendarWidget from "./project-calendar.widget"
import { reminderFetch, reminderGet } from "@/redux/reminder.actions"
import { ReminderState } from "@/redux/reminder.types"
import GetSurveyResultsModal from "@/modals/get-survey-results.modal"
import ProjectFiltersWidget from "./project-filters.widget"
import CardButton from "@/components/card-button"
import ProjectRepeatModal from "@/modals/project-repeat.modal"
import { topicFetch, topicGet } from "@/redux/topic.actions"
import { axisFetch, axisGet } from "@/redux/axis.actions"
import { Axis } from "@/redux/axis.types"
import { Session } from "@/redux/_session.types"
import ToggleButton from "@/components/toggle-button"
import { filterFetch, filterGet } from "@/redux/filter.actions"
import { attributeGet } from "@/redux/attribute.actions"
import { Filter, FilterState } from "@/redux/filter.types"
import { Attribute } from "@/redux/attribute.types"
import { intersection, sortBy } from "lodash"
import UpgradeModal from "@/modals/upgrade.modal"
import SurveyReopenModal from "@/modals/survey-reopen.modal"
import { NavigateFunction, useNavigate } from "react-router-dom"
import ProjectSmsWidget from "./project-sms.widget"
import getLanguagesUtil from "@/utils/get-languages.utils"

interface StateProps extends WithTranslation {
  _session: Session
  email: EmailState
  filter: FilterState
  project: ProjectState
  reminder: ReminderState
  sending: SendingState
  survey: SurveyState
  template: TemplateState
  user: UserState
}

interface Step {
  id: ProjectStep
  icon: IconDefinition
  adminOnly?: boolean
  mode?: string
}

const STEPS: Step[] = [
  { id: "modes", icon: faShareNodes, adminOnly: true },
  { id: "template", icon: faClipboardList },
  { id: "import", icon: faFileImport, adminOnly: true },
  { id: "recipients", icon: faUsers, adminOnly: true },
  { id: "segment", icon: faLayerGroup, adminOnly: true },
  { id: "filters", icon: faFilter, adminOnly: true },
  { id: "languages", icon: faLanguage, adminOnly: true },
  { id: "message", icon: faCommentDots },
  { id: "qrcode", icon: faQrcode, mode: "qrcode", adminOnly: true },
  { id: "sms", icon: faSms, mode: "sms", adminOnly: true },
  { id: "emails", icon: faEnvelope, mode: "email" },
  { id: "calendar", icon: faCalendarDays }
]

//Modal
const MODAL_CLOSE_SURVEY: string = "MODAL_CLOSE_SURVEY" //Close current survey if launched
const MODAL_DELETE: string = "MODAL_DELETE" //Delete current project
const MODAL_GET_RESULTS: string = "MODAL_GET_RESULTS" //Get results for the survey
const MODAL_LAUNCH: string = "MODAL_LAUNCH" //Launch survey
const MODAL_LAUNCH_TEST: string = "MODAL_LAUNCH_TEST" //Launch survey (test mode)
//Memory of the choice "the owl comes back after each step confirmed" (only in this browser)
const REVIEW_GUIDE_DISABLED_KEY: string = "idt-review-guide-disabled"
const REVIEW_AI_DISABLED_KEY: string = "idt-review-ai-disabled"
//Set when the review sends to another page (the questionnaire editor) : the owl welcomes the user back on this page
const REVIEW_IN_PROGRESS_KEY: string = "idt-review-in-progress"
const MODAL_LAUNCH_UNAVAILABLE: string = "MODAL_LAUNCH_UNAVAILABLE" //Review of the project when clicking on launch (unconfirmed steps, errors, warnings)
const MODAL_RENAME: string = "MODAL_RENAME" //Rename the project
const MODAL_REPEAT_PROJECT: string = "MODAL_REPEAT_PROJECT" //Reopen the projet after survey is closed
const MODAL_REOPEN_SURVEY: string = "MODAL_REOPEN_SURVEY" //Reopen the survey after survey is closed
const MODAL_TEST: string = "MODAL_TEST" //Set params for the test
const MODAL_UPGRADE: string = "MODAL_UPGRADE" //Buy modules

function ProjectEditWidget(props: StateProps) {
  const { t } = props
  const navigate: NavigateFunction = useNavigate()

  //Current modal open
  const [currentModal, setCurrentModal] = useState<string | null>(null)

  //Step that is opened
  const isAnimationsDisabled = useAnimationsDisabled()
  const [currentStep, setCurrentStep] = useState<ProjectStep | null>(null)
  const [reviewAnchorStep, setReviewAnchorStep] = useState<ProjectStep | null>(null) //Step open when the owl is called

  //Is the page loaded
  const [isPageLoaded, setIsPageLoaded] = useState(false)

  //Detect loading status by item
  const [itemToLoad, setItemToLoad] = useState<ProjectStep | null>(null)

  //Saving in progress
  const [itemToSave, setItemToSave] = useState<ProjectStep | null>(null)

  //List of steps for the wizard
  const [steps, setSteps] = useState<Step[]>([])

  //Review of the project started from the modal (a line was clicked) : the owl comes back with a small card each time a step is confirmed
  const [isReviewGuided, setIsReviewGuided] = useState<boolean>(false)
  const [isOwlCardShown, setIsOwlCardShown] = useState<boolean>(false) //The card of the owl takes the bottom right : the round button waits
  const [isGuideEnabled, setIsGuideEnabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem(REVIEW_GUIDE_DISABLED_KEY) !== "1"
    } catch {
      return true
    }
  })
  //The owl proposes adjustments from the AI (module of the account, then the choice of the person)
  const [isAiEnabled, setIsAiEnabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem(REVIEW_AI_DISABLED_KEY) !== "1"
    } catch {
      return true
    }
  })
  const confirmedCountRef = useRef<number>(props.project.active.stepsConfirmed.length)

  //Step to show for upgrade
  const [stepToUpgrade, setStepToUpgrade] = useState<ProjectStep>("languages")

  //Update user count
  const [usersCount, setUsersCount] = useState(0)

  //Load emails for the Account
  const loadEmails = useCallback(async () => {
    const emailResponse: any = await store.dispatch(emailFetchByProject(props.project.active.id))
    const emails = emailResponse.error ? [] : emailResponse.map((x) => new Email(x))
    store.dispatch(emailGet(emails, props.project.active.emailsOrder))
    store.dispatch(projectCheckEmails(props.project.active.languages, emails))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.project.active.id, props.project.active.languages])

  //Load filters
  const loadFilters = useCallback(async () => {
    //Create survey with random data true and TemplateId of the project
    store.dispatch(
      surveyActivate(
        new Survey({
          randomData: true,
          template: new Template({
            id: props.project.active.TemplateId ? props.project.active.TemplateId : ""
          })
        })
      )
    )

    //Fetch filters
    const filtersResponse: any = await store.dispatch(filterFetch(true, true))

    //Format filters
    if (filtersResponse.error) {
      store.dispatch(filterGet([]))
      store.dispatch(attributeGet([]))
    } else {
      //Init filters
      const filters: Filter[] = filtersResponse.filter((x) => !x.default)

      //Init attributes
      const attributes: Attribute[] = []
      filters.forEach((filter) => {
        filter.populations.forEach((population, i) => {
          attributes.push(
            new Attribute({
              id: population.name + "_" + filter.name,
              aid: i.toString(),
              name: population.name,
              filterName: filter.name,
              usersCount: population.usersCount
            })
          )
        })
      })

      //Update store
      store.dispatch(filterGet(sortBy(filters, "name")))
      store.dispatch(attributeGet(attributes))
      store.dispatch(projectCheckFilters(filters, props._session.participationMin))
    }
  }, [props._session.participationMin, props.project.active.TemplateId])

  useEffect(() => {
    store.dispatch(projectCheckFilters(props.filter.list, props._session.participationMin))
  }, [props.filter.active.isChecked, props.filter.list, props._session.participationMin])

  //Load recipients data (count and list of users for preview)
  const loadRecipients = useCallback(async () => {
    //Fetch data
    store.dispatch(userStatus(STATUS_SEARCHING))
    const recipients: any = await store.dispatch(
      userFetchRecipients(
        props.project.active.id,
        //Read when the list is loaded : the list itself reloads on a search, a page or a filter (it does not need the project to load again)
        store.getState().user.currentSearch,
        5,
        store.getState().user.currentOffset,
        selectRecipientsFilter(store.getState())
      )
    )
    store.dispatch(userStatus(STATUS_LOADED))

    //Save data into the store
    store.dispatch(userGet(recipients.error ? [] : recipients.users))
    store.dispatch(userGetCountSearch(recipients.error ? 0 : recipients.count))
    store.dispatch(userGetExcluded(recipients.error ? [] : recipients.excludedUsers))
    //The number of files of each filter follows the list (it changes with the selection of the project)
    if (!recipients.error) store.dispatch(recipientsViewActions.setCounts(toRecipientsCounts(recipients)))
    //The number of recipients of the project (the checks must not depend on a search or a filter of the list)
    const recipientsCount = recipients.error ? 0 : recipients.countTotal ?? recipients.count
    setUsersCount(recipientsCount)

    //Check recipients
    if (!recipients.error) {
      store.dispatch(
        projectCheckRecipients(
          recipients.usersWithoutEmail,
          recipients.countWithoutEmail,
          recipients.usersWithoutAddress,
          recipients.countWithoutAddress,
          recipientsCount,
          props._session.participationMin,
          recipients.usersUnreachable || [],
          recipients.countUnreachable || 0,
          props.project.active.diffusionMode,
          props.project.active.qrcodeOptions,
          recipients.countToFix || 0,
          recipients.countLanguageAbsent || 0,
          props.project.active.languages
        )
      )
      store.dispatch(projectCheckImport(recipients.countAccountEmpty || 0, recipients.countAccount ?? 2))
    }
  }, [
    props.project.active.id,
    props._session.participationMin
  ])

  //Load reminders
  const loadReminders = useCallback(async () => {
    const reminderResponse: any = await store.dispatch(reminderFetch(props.project.active.id))
    store.dispatch(reminderGet(reminderResponse.error ? [] : reminderResponse))
  }, [props.project.active.id])

  //Load sendings data
  const loadSendings = useCallback(async () => {
    const sendingResponse: any = await store.dispatch(sendingFetch(props.project.active.id))
    const sendings = sendingResponse.error ? [] : sendingResponse.map((x) => new Sending(x))
    store.dispatch(sendingGet(sendings))
    store.dispatch(projectCheckCalendar(props.project.active.diffusionMode, sendings))
  }, [props.project.active.id, props.project.active.diffusionMode])

  //Load template data
  const loadTemplates = useCallback(async () => {
    //Fetch
    const templateResponse: any = props.project.active.TemplateId
      ? await store.dispatch(templateFetchOne(props.project.active.TemplateId))
      : {}
    const topics: any = templateResponse.id ? await store.dispatch(topicFetch(templateResponse.id)) : []
    const axes: any = templateResponse.id ? await store.dispatch(axisFetch(templateResponse.id, null)) : []

    //Store data
    const template = templateResponse.error ? new Template() : new Template(templateResponse)
    store.dispatch(templateActivate(template))
    store.dispatch(topicGet(topics.error ? [] : topics))
    store.dispatch(axisGet(axes.error ? [] : axes.concat([new Axis({ id: "" })]), template.axesOrder.concat([""])))

    //avoid project having languages that are unavailable in template
    if (props.project.active.languages.some((lang: string) => template.languages.indexOf(lang) < 0)) {
      store.dispatch(projectEdit("languages", intersection(template.languages, props.project.active.languages)))
    }

    //Check template
    store.dispatch(projectCheckTemplate(template, topics, axes, props.project.active.languages))
  }, [props.project.active.TemplateId, props.project.active.languages])

  //Load users
  const loadUsers = useCallback(async () => {
    const usersCountResponse: any = await store.dispatch(userFetchCount("", []))
    const usersCount = usersCountResponse.error ? 0 : usersCountResponse.count
    store.dispatch(userGetCount(usersCount))
  }, [])

  //Load data at start
  //No need to load all the data if survey is launch
  useEffect(() => {
    load()

    async function load() {
      await loadReminders()
      await loadSendings()
      await loadEmails()
      await loadRecipients()
      await loadFilters()
      await loadTemplates()
      await loadUsers()

      if (props.project.active.isLaunch || props.project.active.isClosed) {
        store.dispatch(surveyActivate(props.project.active.lastSurvey))
      }

      setIsPageLoaded(true)
    }
  }, [
    props.project.active.isClosed,
    props.project.active.isLaunch,
    props.project.active.lastSurvey,
    loadEmails,
    loadFilters,
    loadRecipients,
    loadReminders,
    loadSendings,
    loadTemplates,
    loadUsers
  ])

  //Checks that only depend on the project itself : computed at load and on every change, not only when the
  //step is edited (otherwise an unopened step never shows its alerts). Content based key : alerts are edited in place
  const projectChecksKey: string = JSON.stringify([
    props.project.active.languages,
    props.project.active.messageOptions,
    props.project.active.qrcodeOptions,
    props.project.active.smsOptions,
    props.project.active.diffusionMode,
    props.project.active.isSegmentationActive,
    props.project.active.segmentationByAxesRules,
    props.project.active.populations
  ])

  useEffect(() => {
    const project = props.project.active

    //No language selected while the questionnaire offers only one : this one is selected (without opening the step of the languages)
    const templateLanguages: string[] = props.template.active.languages || []
    if (project.id && project.languages.length === 0 && templateLanguages.length === 1) {
      store.dispatch(projectEdit("languages", [templateLanguages[0]]))
      setItemToSave("languages")
      return
    }

    store.dispatch(projectCheckLanguages(project.languages))
    store.dispatch(projectCheckMessage(project.languages, project.messageOptions))
    if (project.diffusionMode.qrcode) store.dispatch(projectCheckQrcode(project.languages, project.qrcodeOptions))
    if (project.diffusionMode.sms) store.dispatch(projectCheckSms(project.smsOptions))
    if (project.isSegmentationActive) {
      store.dispatch(projectCheckSegment(project.segmentationByAxesRules, project.populations))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectChecksKey])

  //Save changes
  //Detect that a save is triggered
  //Declare then item to reload
  useEffect(() => {
    save()
    async function save() {
      if (itemToSave) {
        store.dispatch(projectStatus(STATUS_SAVING))
        await store.dispatch(projectUpdate(props.project.active))
        store.dispatch(projectStatus(STATUS_SAVED))
        setItemToLoad(itemToSave)
        setItemToSave(null)
      }
    }
  }, [props.project.active, itemToSave])

  //Detect change and reload specific data
  useEffect(() => {
    load().then(() => {
      setItemToLoad(null)
    })

    async function load() {
      switch (itemToLoad) {
        case "template":
          return await loadTemplates()
        case "import":
          await loadRecipients()
          await loadFilters()
          return await loadUsers()
        case "recipients":
        case "modes":
        case "qrcode":
          return await loadRecipients()
        case "calendar":
          return await loadSendings()
        default:
          return null
      }
    }
  }, [itemToLoad, loadRecipients, loadFilters, loadSendings, loadTemplates, loadUsers])

  //The page is loaded (the checks are done) : back from the editor during the review
  useEffect(() => {
    if (!isPageLoaded) return
    let isBack: boolean = false
    try {
      isBack = sessionStorage.getItem(REVIEW_IN_PROGRESS_KEY) === "1"
      sessionStorage.removeItem(REVIEW_IN_PROGRESS_KEY)
    } catch {
      //Nothing to restore
    }
    if (isBack && isGuideEnabled) setTimeout(showReviewBackToast, 600)
  }, [isPageLoaded])

  //A step has just been confirmed during the review
  useEffect(() => {
    const count: number = props.project.active.stepsConfirmed.length
    if (isReviewGuided && isGuideEnabled && count > confirmedCountRef.current) showReviewToast()
    confirmedCountRef.current = count
  }, [props.project.active.stepsConfirmed.length])

  //Change steps according change of modes
  useEffect(() => {
    setSteps(
      props._session.userRole === "ADMIN"
        ? STEPS.filter((x) => !x.mode || props.project.active.diffusionMode[x.mode])
        : STEPS.filter((x) => !x.adminOnly)
    )
  }, [props.project.active.diffusionMode])

  //Quit test modal
  function confirmTest(isToLaunch: boolean) {
    setItemToSave("test")
    setCurrentModal(isToLaunch ? MODAL_LAUNCH_TEST : null)
  }

  //Edit projet value
  function editProject(key: string, value: any, step: ProjectStep) {
    store.dispatch(projectEdit(key, value))
    setItemToSave(step)
  }

  //Display subtitle for every step
  function getSubtitle(step: ProjectStep) {
    switch (step) {
      case "template":
        return props.template.active.label
      case "modes":
        return props.project.active.diffusionModeLabel
      case "import":
        return t("survey_recipients_user_count", { count: props.user.count, s: props.user.count > 1 ? "s" : "" })
      case "filters":
        return t("filters_custom_count", {
          count: props.filter.list.length,
          s: props.filter.list.length > 1 ? "s" : ""
        })
      case "recipients":
        return t("survey_recipients_count", { count: usersCount, s: usersCount > 1 ? "s" : "" })
      case "segment":
        return props.project.active.isSegmentationActive
          ? t("project_segmentation_actived")
          : t("project_segmentation_inactive")
      case "languages": {
        const { languages, messageOptions } = props.project.active

        const langCodes = languages.length > 0 ? languages.join(", ").toUpperCase() : ""
        const pivotLang = messageOptions.commentPivotLanguage
        const translationEnabled = messageOptions.autoTranslateComment
        const pivotLabel = pivotLang ? getLanguagesUtil([pivotLang])[0]?.fullName : null

        if (translationEnabled && pivotLabel) {
          return (
            <div className="flex-col">
              <div>{langCodes}</div>
              <div style={{ fontSize: "0.85em", color: "#666" }}>
                {t("project_message_translation_auto_target_label_short")}: {pivotLabel}
              </div>
            </div>
          )
        }

        return langCodes
      }
      case "emails":
        return t("email_count", { count: props.email.list.length, s: props.email.list.length > 1 ? "s" : "" })
      case "calendar":
        return props.project.active.getNextEvent(props.sending.list, props.reminder.list, true, props.email.list).name
      default:
        return <span />
    }
  }

  //get errors but hide those from inactive sending modes
  function getErrorsFiltered(): string[] {
    return props.project.active.errorsFlatten.filter((error: string) =>
      props.project.active.inactiveDiffusionModes.every(
        (inactiveMode: string) => !error.includes("project_error_" + inactiveMode)
      )
    )
  }

  //get warnings but hide those from inactive sending modes
  function getWarningsFiltered(): string[] {
    return props.project.active.warningsFlatten.filter((warning: string) =>
      props.project.active.inactiveDiffusionModes.every(
        (inactiveMode: string) => !warning.includes("project_warning_" + inactiveMode)
      )
    )
  }

  function getUnconfirmedSteps() {
    if (props._session.userRole === "ADMIN") {
      return STEPS.filter(
        (x: Step) =>
          !isPaywall(x.id) &&
          (!x.mode || props.project.active.diffusionMode[x.mode]) &&
          props.project.active.stepsConfirmed.indexOf(x.id) < 0
      )
    } else {
      return STEPS.filter((x) => !x.adminOnly && props.project.active.stepsConfirmed.indexOf(x.id) < 0)
    }
  }

  function getWidget() {
    switch (currentStep) {
      case "template":
        return <ProjectTemplateWidget onNext={nextStep} onEdit={() => setItemToSave("template")} />
      case "modes":
        return <ProjectModesWidget onNext={nextStep} onEdit={() => setItemToSave("modes")} />
      case "import":
        return <ProjectImportWidget onNext={nextStep} onEdit={() => setItemToSave("import")} />
      case "filters":
        return <ProjectFiltersWidget onNext={nextStep} onEdit={() => openStep("import")} />
      case "recipients":
        return <ProjectRecipientsWidget onNext={nextStep} onEdit={() => setItemToSave("recipients")} />
      case "segment":
        return <ProjectSegmentWidget onNext={nextStep} onSelect={() => setItemToSave("segment")} />
      case "languages":
        return <ProjectLanguagesWidget onNext={nextStep} onEdit={() => setItemToSave("languages")} />
      case "message":
        return <ProjectMessageWidget onNext={nextStep} onEdit={() => setItemToSave("message")} />
      case "qrcode":
        return <ProjectQrcodeWidget onNext={nextStep} onEdit={() => setItemToSave("qrcode")} />
      case "sms":
        return <ProjectSmsWidget onNext={nextStep} onEdit={() => setItemToSave("sms")} />
      case "emails":
        return <ProjectEmailsWidget onNext={nextStep} onEdit={() => setItemToSave("emails")} />
      case "calendar":
        return <ProjectCalendarWidget onNext={nextStep} onEdit={() => setItemToSave("calendar")} />
      default:
        return <div>{t("widget_not_found")}</div>
    }
  }

  function isPaywall(stepId: ProjectStep) {
    return props.project.active.paywallSteps.indexOf(stepId) > -1 && !props._session.modules.survey[stepId]
  }

  //Detect if the step is done
  function isStepConfirmed(stepId: ProjectStep) {
    return props.project.active.stepsConfirmed.indexOf(stepId) > -1
  }

  //Detect if the step is opened
  function isStepVisited(stepId: ProjectStep) {
    return props.project.active.stepsVisited.indexOf(stepId) > -1
  }

  //On click on next
  //Save if step have to be visited / confirmed
  async function nextStep(isVisited, isConfirmed) {
    if (currentStep) {
      //Mark step as confirmed
      if (isConfirmed && !isStepConfirmed(currentStep)) {
        store.dispatch(projectEdit("stepsConfirmed", props.project.active.stepsConfirmed.concat([currentStep])))
      }
      //Mark step as unconfirmed
      else if (!isConfirmed && isStepConfirmed(currentStep)) {
        store.dispatch(
          projectEdit(
            "stepsConfirmed",
            props.project.active.stepsConfirmed.filter((x) => x !== currentStep)
          )
        )
      }

      //Mark step as visited
      if (isVisited && !isStepVisited(currentStep)) {
        store.dispatch(projectEdit("stepsVisited", props.project.active.stepsVisited.concat([currentStep])))
      }
      //Mark step as not visited
      else if (!isVisited && isStepVisited(currentStep)) {
        store.dispatch(
          projectEdit(
            "stepsVisited",
            props.project.active.stepsVisited.filter((x) => x !== currentStep)
          )
        )
      }

      //Set item to save
      setItemToSave(currentStep)

      //Scroll on top the step
      scroll(currentStep)

      //Close step
      setCurrentStep(null)
    }
  }

  //Click on launch button
  function launch() {
    stopReview()
    setCurrentStep(null)
    setCurrentModal(MODAL_LAUNCH)
  }

  //Click on the button to open the calendar
  function openCalendar() {
    openStep("calendar")
    setTimeout(() => {
      scroll("calendar")
    }, 200)
  }

  //Open step
  //Mark step as undone
  function openStep(stepId: ProjectStep) {
    //first check if module is available
    if (isPaywall(stepId)) {
      setStepToUpgrade(stepId)
      setCurrentModal(MODAL_UPGRADE)
    } else {
      const isOpening = stepId !== currentStep
      setCurrentStep(isOpening ? stepId : null)
      //The opened step goes to the top of the page (after the previous step closed and the new one is drawn)
      if (isOpening) setTimeout(() => scroll(stepId), 350)
    }
  }

  //Open a step from a link (never closes it) and scroll to it
  function showStep(stepId: ProjectStep) {
    if (isPaywall(stepId)) {
      setStepToUpgrade(stepId)
      setCurrentModal(MODAL_UPGRADE)
    } else {
      setCurrentStep(stepId)
      setTimeout(() => scroll(stepId), 300)
    }
  }

  //Alerts of the steps with the step they come from (the lines of the modal that lists them lead to the step)
  function getAlertLines(kind: "errors" | "warnings"): { step: ProjectStep; message: string }[] {
    const prefix = kind === "errors" ? "project_error_" : "project_warning_"
    return Object.entries(props.project.active.alerts)
      //The warnings of a step the user has turned off ("Afficher les avertissements pour cette étape") are not asked again
      .filter(([step]) => kind === "errors" || props.project.active.isStepWarningActive(step as ProjectStep))
      .flatMap(([step, alert]: [string, any]) => (alert[kind] as string[]).map((message) => ({ step: step as ProjectStep, message })))
      .filter((line) => props.project.active.inactiveDiffusionModes.every((mode: string) => !line.message.includes(prefix + mode)))
  }

  //End of the review in progress : no more card of the owl until a line of the review is clicked again
  function stopReview() {
    setIsReviewGuided(false)
    toast.dismiss("project-review")
  }

  //Back from the questionnaire editor during the review : the owl welcomes the user back and offers to go on
  function showReviewBackToast() {
    const rows: LaunchReviewRow[] = getLaunchReviewRows()
    const errorsCount: number = rows.reduce((sum, row) => sum + countRowErrors(row), 0)
    const warningsCount: number = rows.reduce((sum, row) => sum + countRowWarnings(row), 0)
    const unconfirmedCount: number = rows.filter((row) => row.isUnconfirmed).length
    const left: string = [
      errorsCount > 0 && t("project_review_errors", { count: errorsCount, s: errorsCount > 1 ? "s" : "" }),
      unconfirmedCount > 0 && t("project_review_unconfirmed", { count: unconfirmedCount, s: unconfirmedCount > 1 ? "s" : "" }),
      warningsCount > 0 && t("project_review_warnings", { count: warningsCount, s: warningsCount > 1 ? "s" : "" })
    ]
      .filter(Boolean)
      .join(" · ")
    setIsOwlCardShown(true)
    toast(
      <ProjectReviewToast
        title={t("project_review_back_title")}
        subtitle={left || t("project_review_back_done")}
        actionLabel={t("project_review_back_continue")}
        actionIcon={faListCheck}
        onAction={() => {
          toast.dismiss("project-review")
          setIsReviewGuided(true)
          setCurrentModal(MODAL_LAUNCH_UNAVAILABLE)
        }}
        stopLabel={t("project_review_toast_stop")}
        onStop={() => changeGuide(false)}
      />,
      {
        toastId: "project-review",
        autoClose: 20000,
        closeOnClick: false,
        icon: false,
        className: "project-review-toast",
        position: "bottom-right",
        onClose: () => setIsOwlCardShown(false)
      }
    )
  }

  //The user does not want the owl to come back after each step (or wants it back) : kept in this browser
  function changeAi(isEnabled: boolean) {
    setIsAiEnabled(isEnabled)
    try {
      if (isEnabled) localStorage.removeItem(REVIEW_AI_DISABLED_KEY)
      else localStorage.setItem(REVIEW_AI_DISABLED_KEY, "1")
    } catch {
      //The choice only lasts until the page is reloaded
    }
    //The list of the assistant follows the switch at once (the questionnaire is already loaded)
    const state = store.getState()
    if (state.template.active.id) {
      store.dispatch(projectCheckTemplate(state.template.active, state.topic.list, state.axis.list, props.project.active.languages))
    }
  }

  function changeGuide(isEnabled: boolean) {
    setIsGuideEnabled(isEnabled)
    try {
      if (isEnabled) localStorage.removeItem(REVIEW_GUIDE_DISABLED_KEY)
      else localStorage.setItem(REVIEW_GUIDE_DISABLED_KEY, "1")
    } catch {
      //The choice only lasts until the page is reloaded
    }
    if (!isEnabled) stopReview()
  }

  //Small card of the owl after a step is confirmed : what is left in it, and the next step to look at
  function showReviewToast() {
    const rows: LaunchReviewRow[] = getLaunchReviewRows()
    const confirmedId: string = props.project.active.stepsConfirmed[props.project.active.stepsConfirmed.length - 1]
    const confirmed: LaunchReviewRow | undefined = rows.find((row) => row.id === confirmedId)
    const toSee: LaunchReviewRow[] = rows.filter((row) => getSeverity(row) !== "ok")
    const readyCount: number = rows.length - toSee.length
    const isBlocked: boolean = rows.some((row) => row.errors.length > 0 || row.isUnconfirmed)

    //Next step to look at : the first one after the confirmed step, or the first one of the list
    const confirmedIndex: number = rows.findIndex((row) => row.id === confirmedId)
    const next: LaunchReviewRow | undefined =
      toSee.find((row) => rows.findIndex((r) => r.id === row.id) > confirmedIndex) || toSee.find((row) => row.id !== confirmedId)

    const warningsLeft: number = confirmed?.warnings.length || 0
    setIsOwlCardShown(true)
    toast(
      <ProjectReviewToast
        title={t("project_review_toast_done")}
        progress={{ ready: readyCount, total: rows.length }}
        subtitle={
          warningsLeft > 0
            ? t("project_review_toast_left", { count: warningsLeft, s: warningsLeft > 1 ? "s" : "" })
            : t("project_review_toast_progress", { ready: readyCount, total: rows.length })
        }
        actionLabel={
          next ? t("project_review_toast_next", { name: t("project_setup_" + next.id) }) : !isBlocked ? t("project_review_toast_create") : undefined
        }
        actionIcon={next ? next.icon : faPaperPlane}
        onAction={() => {
          toast.dismiss("project-review")
          if (next) showStep(next.id as ProjectStep)
          else launch()
        }}
        reviewLabel={t("project_review_toast_review")}
        onReview={() => {
          toast.dismiss("project-review")
          setCurrentModal(MODAL_LAUNCH_UNAVAILABLE)
        }}
        stopLabel={t("project_review_toast_stop")}
        onStop={() => changeGuide(false)}
      />,
      {
        toastId: "project-review",
        autoClose: 15000,
        closeOnClick: false,
        icon: false,
        className: "project-review-toast",
        position: "bottom-right",
        onClose: () => setIsOwlCardShown(false)
      }
    )
  }

  //A message of the review : the step opens, then the place to fix is shown (the questionnaire editor opens straight away)
  function goToAlertFromReview(stepId: string, message: string, language?: string) {
    //The targets are keyed by the text of the message : a message kept as a translation key is translated first
    const targets = props.project.active.alerts[stepId as ProjectStep]?.targets
    let target: string | undefined = targets?.[message] ?? targets?.[t(message)]
    //A chip of language : the same place, in that language
    if (target && language && target.startsWith("focus:")) target = "focus:" + target.replace("focus:", "").split("|")[0] + "|" + language
    setIsReviewGuided(true)
    setCurrentModal(null)
    const isAnotherPage: boolean = !!target && (target.startsWith("topic:") || target === "params" || target === "editor")
    if (isAnotherPage) {
      try {
        sessionStorage.setItem(REVIEW_IN_PROGRESS_KEY, "1")
      } catch {
        //No welcome back card then
      }
    } else showStep(stepId as ProjectStep)
    if (target) setTimeout(() => goToAlertTarget(target, { step: stepId as ProjectStep, navigate, openStep: showStep }), isAnotherPage ? 0 : 700)
  }

  //A line per step for the review before the survey is created (the steps of the sending modes not used are left out)
  function getLaunchReviewRows(): LaunchReviewRow[] {
    const errors = getAlertLines("errors")
    const warnings = getAlertLines("warnings")
    const unconfirmed: string[] = getUnconfirmedSteps().map((step: Step) => step.id)
    return steps
      .filter((step: Step) => !isPaywall(step.id))
      .map((step: Step) => ({
        id: step.id,
        icon: step.icon,
        errors: errors.filter((line) => line.step === step.id).map((line) => line.message),
        warnings: warnings.filter((line) => line.step === step.id).map((line) => line.message),
        isUnconfirmed: unconfirmed.indexOf(step.id) > -1,
        errorsCount: countAlertErrors(props.project.active.alerts[step.id as ProjectStep]),
        warningsCount: countAlertWarnings(props.project.active.alerts[step.id as ProjectStep]),
        languages: props.project.active.alerts[step.id as ProjectStep]?.languages,
        ai: props.project.active.alerts[step.id as ProjectStep]?.ai
      }))
  }

  //Scroll to specifi step
  function scroll(step: ProjectStep) {
    document.querySelector("#step-" + step)?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    })
  }

  //Click on test button
  function test() {
    setCurrentStep(null)
    setCurrentModal(MODAL_TEST)
  }

  return (
    <ProjectStepContext.Provider value={{ openStep: showStep }}>
    <PageLoader status={isPageLoaded ? STATUS_LOADED : STATUS_LOADING} skeleton={<ProjectEditSkeletonWidget />}>
      {currentModal === MODAL_CLOSE_SURVEY && <ProjectCloseSurveyModal onClose={() => setCurrentModal(null)} />}

      {currentModal === MODAL_DELETE && <ProjectDeleteModal onClose={() => setCurrentModal(null)} />}

      {currentModal === MODAL_GET_RESULTS && (
        <GetSurveyResultsModal onClose={() => setCurrentModal(null)} destination="/dashboard/overview" />
      )}

      {currentModal === MODAL_LAUNCH && (
        <ProjectLaunchModal onClose={() => setCurrentModal(null)} recipientsCount={usersCount} />
      )}

      {currentModal === MODAL_LAUNCH_TEST && (
        <ProjectLaunchModal isTest onClose={() => setCurrentModal(null)} recipientsCount={usersCount} />
      )}

      {currentModal === MODAL_LAUNCH_UNAVAILABLE && (
        <ProjectLaunchReviewModal
          rows={getLaunchReviewRows()}
          onClose={() => {
            setReviewAnchorStep(null)
            setCurrentModal(null)
          }}
          onGoTo={(stepId: string) => {
            setReviewAnchorStep(null)
            setIsReviewGuided(true)
            setCurrentModal(null)
            showStep(stepId as ProjectStep)
          }}
          onGoToMessage={goToAlertFromReview}
          isGuideEnabled={isGuideEnabled}
          onGuideChange={changeGuide}
          isAiAvailable={props._session.modules.survey.projectAssistant}
          isAiEnabled={isAiEnabled}
          onAiChange={changeAi}
          onLaunch={launch}
          initialStepId={reviewAnchorStep || undefined}
        />
      )}

      {currentModal === MODAL_RENAME && (
        <ProjectRenameModal
          onClose={(isSaved: boolean, projectName: string) => {
            if (isSaved) editProject("name", projectName, "name")
            setCurrentModal(null)
          }}
        />
      )}

      {currentModal === MODAL_REOPEN_SURVEY && <SurveyReopenModal onClose={() => setCurrentModal(null)} />}

      {currentModal === MODAL_REPEAT_PROJECT && <ProjectRepeatModal onClose={() => setCurrentModal(null)} />}

      {currentModal === MODAL_TEST && (
        <ProjectTestModal onClose={() => confirmTest(false)} onNext={() => confirmTest(true)} />
      )}

      {currentModal === MODAL_UPGRADE && <UpgradeModal feature={stepToUpgrade} onClose={() => setCurrentModal(null)} />}

      <PageHeader title={props.project.active.name} subtitle={t("project_help")}></PageHeader>

      {props.project.active.status === "test" || props.project.active.status === "launch" ? (
        <div className="flex1 flex">
          <div className="flex flex-dcol" style={{ width: 700 }}>
            <Space />
            <ProjectChecklistWidget />
            <Space />
          </div>

          <div className="width-20" />

          <div className="flex" style={{ marginRight: "-14px" }}>
            <CardButton
              icon={faChartBar}
              isSmall
              onClick={() => setCurrentModal(MODAL_GET_RESULTS)}
              title={t("survey_link_dashboard")}
            />

            <CardButton icon={faCalendar} isSmall onClick={() => openCalendar()} title={t("project_calendar")} />

            <CardButton
              icon={faUserCog}
              isSmall
              isCta
              onClick={() => navigate("/project/invitations")}
              title={t("project_invitations")}
            />
          </div>
        </div>
      ) : (
        <div className="flex1 flex">
          <Space />
          <div style={{ width: 700 }}>
            <ProjectChecklistWidget />
          </div>
          <Space />
        </div>
      )}

      <div className="height-60" />

      {props.project.active.status === "closed" ? (
        <div className="flex">
          <Space />

          <CardButton
            onClick={() => (props.survey.active.id.length > 0 ? setCurrentModal(MODAL_GET_RESULTS) : {})}
            subtitle={props.survey.active.id.length < 1 ? t("survey_do_not_exist") : undefined}
            icon={faChartBar}
            isActive={props.survey.active.id.length > 0}
            isLock={props.survey.active.id.length < 1}
            title={t("survey_link_dashboard")}
          />

          <CardButton
            onClick={() => navigate("/project/invitations")}
            icon={faUserCog}
            title={t("project_invitations")}
          />

          <CardButton onClick={() => setCurrentModal(MODAL_REPEAT_PROJECT)} icon={faEdit} title={t("project_repeat")} />

          <CardButton
            onClick={() => setCurrentModal(MODAL_REOPEN_SURVEY)}
            icon={faPaperPlane}
            title={t("survey_reopen")}
          />

          <CardButton onClick={() => setCurrentModal(MODAL_DELETE)} icon={faTrash} title={t("project_delete")} />

          <Space />
        </div>
      ) : (
        <div>
          {steps.map((step: Step, index: number) => (
            <motion.div
              key={step.id}
              id={"step-" + step.id}
              style={{ scrollMarginTop: 8 }}
              initial={isAnimationsDisabled ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: Math.min(index, 8) * 0.06, ease: [0.22, 1, 0.36, 1] }}>
              <StepCard
                isLocked={props.project.active.isLocked(step.id) || isPaywall(step.id)}
                title={t("project_setup_" + step.id)}
                subtitle={getSubtitle(step.id)}
                icon={step.icon}
                showStepIcon
                isConfirmed={isStepConfirmed(step.id)}
                isVisited={isStepVisited(step.id)}
                isOpen={step.id === currentStep}
                onOpen={() => openStep(step.id)}
                showWarnings={props.project.active.showWarnings && props.project.active.isStepWarningActive(step.id)}
                tooltip={
                  isPaywall(step.id)
                    ? "project_step_paywall"
                    : props.project.active.isLocked(step.id)
                      ? "project_step_unavailable"
                      : ""
                }
                alert={props.project.active.alerts[step.id]}>
                {getWidget()}
              </StepCard>
            </motion.div>
          ))}

          {props._session.userRole === "ADMIN" && (
            <div className="flex">
              <div className="flex flex-dcol">
                <Space />
                <ToggleButton
                  color="#F2D600"
                  isActive={props.project.active.showWarnings}
                  onToogle={() => store.dispatch(projectEdit("showWarnings", !props.project.active.showWarnings))}
                />
                <Space />
              </div>

              <div className="width-10" />

              <div className="flex flex-dcol">
                <Space />
                {t("project_show_warnings") +
                  (props.project.active.stepsVisited.length > 1 && getWarningsFiltered().length > 1
                    ? ` (${getWarningsFiltered().length})`
                    : "")}
                <Space />
              </div>
            </div>
          )}

          <div className="flex">
            <div className="flex flex-dcol">
              <Space />

              <div className="height-10" />

              <Link isWithoutMargin onClick={() => setCurrentModal(MODAL_RENAME)}>
                {t("project_rename")}
              </Link>

              <div className="height-10" />

              <Link isWithoutMargin onClick={() => setCurrentModal(MODAL_DELETE)}>
                {t("project_delete")}
              </Link>

              <Space />
            </div>

            <Space />

            {props.project.active.status === "draft" ? (
              <div className="flex">
                <Button
                  tooltip={t("project_edit_launch_test_description", {
                    count: props._session.accountOptions.ceilsTesters
                  })}
                  className={"secondary"}
                  icon={faVial}
                  isFullWidth
                  isWithBorder
                  isLarge
                  onClick={test}>
                  {" " + t("project_test")}
                </Button>

                <div className="width-20" />

                <Button
                  tooltip={t("project_edit_launch_description")}
                  className={
                    getUnconfirmedSteps().length === 0 && getErrorsFiltered().length === 0 ? "primary" : "light"
                  }
                  icon={getUnconfirmedSteps().length === 0 && getErrorsFiltered().length === 0 ? faPaperPlane : faLock}
                  isFullWidth
                  isWithBorder
                  isLarge
                  onClick={() =>
                    getUnconfirmedSteps().length === 0 && getErrorsFiltered().length === 0 && getAlertLines("warnings").length === 0
                      ? launch()
                      : setCurrentModal(MODAL_LAUNCH_UNAVAILABLE)
                  }>
                  {" " + t("project_launch")}
                </Button>
              </div>
            ) : (
              <div>
                <Button
                  className="secondary"
                  icon={faStop}
                  tooltip={t(
                    props.project.active.status === "test"
                      ? "project_edit_close_test_description"
                      : "project_edit_close_description"
                  )}
                  isFullWidth
                  isWithBorder
                  isLarge
                  onClick={() => setCurrentModal(MODAL_CLOSE_SURVEY)}>
                  {" " + t(props.project.active.status === "test" ? "project_edit_close_test" : "project_edit_close")}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      {props.project.active.status === "draft" && !currentModal && !isOwlCardShown && (
        <ProjectReviewLauncher
          label={t("project_review_launcher")}
          errorsCount={getLaunchReviewRows().reduce((sum, row) => sum + countRowErrors(row), 0)}
          warningsCount={getLaunchReviewRows().reduce((sum, row) => sum + countRowWarnings(row), 0)}
          onClick={() => {
            setReviewAnchorStep(currentStep)
            setIsReviewGuided(true)
            setCurrentModal(MODAL_LAUNCH_UNAVAILABLE)
          }}
        />
      )}
    </PageLoader>
    </ProjectStepContext.Provider>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  email: state.email,
  filter: state.filter,
  project: state.project,
  reminder: state.reminder,
  sending: state.sending,
  survey: state.survey,
  template: state.template,
  user: state.user
})

export default connect(mapStateToProps)(withTranslation()(ProjectEditWidget))
